import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Image } from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useSlideImages } from '../hooks/useSlideImages';
import { SlideImage } from '../types';

interface SlideFormData {
  url: string;
  alt: string;
  order: number;
  isActive: boolean;
}

const initialFormData: SlideFormData = {
  url: '',
  alt: '',
  order: 0,
  isActive: true,
};

export const SlideManagement: React.FC = () => {
  const { slideImages, loading, error, createSlideImage, updateSlideImage, deleteSlideImage } = useSlideImages();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<SlideImage | null>(null);
  const [formData, setFormData] = useState<SlideFormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);

  const handleOpenDialog = (slide?: SlideImage) => {
    if (slide) {
      setEditingSlide(slide);
      setFormData({
        url: slide.url,
        alt: slide.alt || '',
        order: slide.order,
        isActive: slide.isActive,
      });
    } else {
      setEditingSlide(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSlide(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (editingSlide) {
        await updateSlideImage(editingSlide.id, formData);
      } else {
        await createSlideImage(formData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save slide:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('このスライド画像を削除しますか？')) {
      try {
        await deleteSlideImage(id);
      } catch (error) {
        console.error('Failed to delete slide:', error);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'url',
      headerName: '画像',
      width: 100,
      headerAlign: 'center',
      renderCell: (params) => (
        <Box 
          sx={{ 
            width: '100%',
            height: '100%',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center'
          }}
        >
          <Box 
            sx={{ 
              width: 60, 
              height: 40, 
              bgcolor: 'grey.200', 
              borderRadius: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center'
            }}
          >
            <Image fontSize="small" color="disabled" />
          </Box>
        </Box>
      ),
    },
    { field: 'alt', headerName: 'Alt テキスト', width: 200, headerAlign: 'center' },
    { field: 'order', headerName: '順序', width: 100, headerAlign: 'center' },
    {
      field: 'isActive',
      headerName: '状態',
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value ? 'アクティブ' : '非アクティブ'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: '作成日',
      width: 150,
      headerAlign: 'center',
      renderCell: (params) => new Date(params.value).toLocaleDateString('ja-JP'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '操作',
      width: 120,
      headerAlign: 'center',
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="編集"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="削除"
          onClick={() => handleDelete(params.id as string)}
        />,
      ],
    },
  ];

  if (loading && slideImages.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          スライド画像管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          新規追加
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={slideImages}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          rowHeight={60}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSlide ? 'スライド画像を編集' : '新しいスライド画像を追加'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="画像URL"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Alt テキスト"
            value={formData.alt}
            onChange={(e) => setFormData({ ...formData, alt: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="表示順序"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            margin="normal"
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="アクティブ"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.url}
          >
            {submitting ? <CircularProgress size={20} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};